import axios from 'axios';
import Cookies from 'js-cookie';

const GOOGLE_DRIVE_API = 'https://www.googleapis.com/drive/v3/files';
const GOOGLE_DRIVE_UPLOAD_API =
  'https://www.googleapis.com/upload/drive/v3/files';

const getAuthHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

export const getToken = () => {
  return Cookies.get('authToken');
};

const getFolderIdByName = async (
  token: string,
  folderName: string,
): Promise<string | null> => {
  const query = `name='${folderName}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;

  try {
    const { data } = await axios.get<{ files: { id: string }[] }>(
      GOOGLE_DRIVE_API,
      {
        params: { q: query, fields: 'files(id)' },
        headers: getAuthHeaders(token),
      },
    );

    return data.files.length > 0 ? data.files[0].id : null;
  } catch (error) {
    console.error('Error fetching folder ID:', error);
    throw error;
  }
};

const createFolderIfNotExists = async (
  token: string,
  folderName: string,
  parentId?: string,
): Promise<string> => {
  const existingFolderId = await getFolderIdByName(token, folderName);
  if (existingFolderId) return existingFolderId;

  try {
    const { data } = await axios.post<{ id: string }>(
      GOOGLE_DRIVE_API,
      {
        name: folderName,
        mimeType: 'application/vnd.google-apps.folder',
        parents: parentId ? [parentId] : [],
      },
      { headers: getAuthHeaders(token) },
    );

    return data.id;
  } catch (error) {
    console.error('Error creating folder:', error);
    throw error;
  }
};

const setPermissions = async (token: string, fileId: string): Promise<void> => {
  try {
    await axios.post(
      `${GOOGLE_DRIVE_API}/${fileId}/permissions`,
      { role: 'reader', type: 'anyone' },
      { headers: getAuthHeaders(token) },
    );
  } catch (error) {
    console.error('Error setting permissions:', error);
    throw error;
  }
};

export const uploadFileToGoogleDrive = async (
  token: string,
  files: File[],
  childFolderName: string,
): Promise<string[]> => {
  try {
    const parentFolderName = 'documents';
    const parentFolderId = await createFolderIfNotExists(
      token,
      parentFolderName,
    );
    const childFolderId = await createFolderIfNotExists(
      token,
      childFolderName,
      parentFolderId,
    );

    const uploadedUrls: string[] = [];

    for (const file of files) {
      const metadata = {
        name: file.name,
        mimeType: file.type,
        parents: [childFolderId],
      };

      const formData = new FormData();
      formData.append(
        'metadata',
        new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
      );
      formData.append('file', file);

      const { data } = await axios.post<{ id: string }>(
        `${GOOGLE_DRIVE_UPLOAD_API}?uploadType=multipart`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      await setPermissions(token, data.id);

      uploadedUrls.push(
        `https://drive.google.com/file/d/${data.id}/view?usp=drive_link&name=${encodeURIComponent(file.name)}`,
      );
    }

    return uploadedUrls;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

const deleteGoogleFileOrFolder = async (
  token: string,
  fileId: string,
): Promise<void> => {
  try {
    await axios.delete(`${GOOGLE_DRIVE_API}/${fileId}`, {
      headers: getAuthHeaders(token),
    });
  } catch (error) {
    console.error('Error deleting file/folder:', error);
    throw error;
  }
};

export const deleteGoogleFolder = async (
  token: string,
  folderName: string,
): Promise<void> => {
  const folderId = await getFolderIdByName(token, folderName);
  if (!folderId) throw new Error(`Folder "${folderName}" not found`);

  await deleteGoogleFileOrFolder(token, folderId);
};

export const deleteGoogleFile = async (
  token: string,
  fileUrl: string,
): Promise<void> => {
  const match = fileUrl.match(/\/d\/([^/]+)/);
  const fileId = match ? match[1] : null;
  if (!fileId) throw new Error('Invalid Google Drive file URL');

  await deleteGoogleFileOrFolder(token, fileId);
};
